import { ApplicationView, useController } from '@aha-app/mvc';
import React, { Fragment, useState } from 'react';
import Modal, { ModalHeader } from '../../utils/Modal';
import styles from './create-record-link-modal.module.css';
import Select from '../../components/common/SelectField';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import RecordLinkController from '../../controllers/RecordLinkController';
import classnames from 'classnames';
import type {
  WhiteboardRecordWithRefNum,
  RecordLinkItem,
  RecordLinkOption,
} from '../../types';

const SelectComponent: React.FC<{
  linkOptions: RecordLinkOption[];
  linkType: RecordLinkOption;
  setLinkType: (link: RecordLinkOption) => void;
  style?: React.CSSProperties;
}> = ({ linkOptions, linkType, setLinkType, style }) => {
  return (
    <Select
      initialValue={linkType.text}
      getOptions={async () => {
        return {
          options: linkOptions.map(option => ({
            key: option.text,
            value: option.text,
            text: option.text,
            item: option,
          })),
        };
      }}
      hideSearchBar
      multiple={false}
      setValue={value => setLinkType(value.item)}
      style={style}
    />
  );
};

const Row: React.FC<{
  record: WhiteboardRecordWithRefNum;
}> = ({ record }) => {
  return (
    <span className={styles.rowRecord}>
      {record.referenceNum && (
        <>
          <span className={styles.rowRef}>{record.referenceNum}</span>{' '}
        </>
      )}
      <span className={styles.rowName}>{record.name}</span>
    </span>
  );
};

const SingleItemBody: React.FC<{
  item: RecordLinkItem;
  linkOptions: RecordLinkOption[];
  linkType: RecordLinkOption;
  setLinkType: (link: RecordLinkOption) => void;
}> = ({ item, linkOptions, linkType, setLinkType }) => {
  const parentRecord = item.parentRecord;
  const childRecord = item.childRecord;
  if (!parentRecord || !childRecord) {
    return null;
  }
  return (
    <>
      <div className={styles.row}>
        <span className={styles.rowHeader}>This record</span>
        <Row record={parentRecord} />
      </div>
      <SelectComponent
        linkOptions={linkOptions}
        linkType={linkType}
        setLinkType={setLinkType}
        // Simpler to just specify width and margin to line things up here
        style={{ width: 270, marginLeft: 140 }}
      />
      <div className={styles.row}>
        <span className={styles.rowHeader}>This record</span>
        <Row record={childRecord} />
      </div>
    </>
  );
};

const MultiItemBody: React.FC<{
  items: RecordLinkItem[];
  linkOptions: RecordLinkOption[];
  linkType: RecordLinkOption;
  setLinkType: (link: RecordLinkOption) => void;
}> = ({ items, linkOptions, linkType, setLinkType }) => {
  return (
    <>
      <aha-alert>Dependency type applies to all selected connections</aha-alert>
      <div className={styles.dependencyWrapper}>
        <div className={styles.dependencyTitle}>Dependency type</div>
        <SelectComponent
          linkOptions={linkOptions}
          linkType={linkType}
          setLinkType={setLinkType}
          style={{ flex: 0.45 }}
        />
      </div>
      <div className={styles.recordSection}>
        <div className={styles.recordTitle}>Records</div>
        <div className={styles.recordWrapper}>
          {items.map((item, index) => {
            const { parentRecord, childRecord } = item;
            if (!parentRecord || !childRecord) {
              return null;
            }
            return (
              <Fragment key={parentRecord.id + childRecord.id}>
                <Row record={parentRecord} />
                <Row record={childRecord} />
                {index < items.length - 1 && <div className={styles.divider} />}
              </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};

const RecordLinkModal: React.FC = () => {
  const controller = useController(RecordLinkController);
  const linkOptions = controller.supportedRecordLinkOptions() ?? [];
  const [linkType, setLinkType] = useState(linkOptions[0]);
  const { recordLinkItems, saving, loading } = controller.state;

  const useSingleItemBody = recordLinkItems.length === 1;
  // Set height manually to fit the spinner in the default min height of the modal body
  const loadingHeight = useSingleItemBody ? 176 : 353;

  const body = () => {
    if (useSingleItemBody) {
      return (
        <SingleItemBody
          item={recordLinkItems[0]}
          linkOptions={linkOptions}
          linkType={linkType}
          setLinkType={setLinkType}
        />
      );
    }
    return (
      <MultiItemBody
        items={recordLinkItems}
        linkOptions={linkOptions}
        linkType={linkType}
        setLinkType={setLinkType}
      />
    );
  };

  const onClose = () => controller.actionResetRecordLinkState();

  return (
    <Modal onClose={onClose}>
      <div className={classnames(styles.modalWrapper)}>
        <ModalHeader onClose={onClose} modalTitle='Create dependency' />
        {loading && (
          <div
            className={styles.modalLoading}
            style={{ height: loadingHeight }}
          >
            <aha-spinner size='32px' />
          </div>
        )}
        {!loading && <div className={styles.modalBody}>{body()}</div>}
        <aha-modal-footer>
          <aha-button
            kind='primary'
            type='submit'
            disabled={customElementBoolAttr(!loading && saving)}
            onClick={() => {
              void controller.actionCreateRecordLinks(linkType);
            }}
          >
            {saving ? 'Saving...' : 'Create dependency'}
          </aha-button>
        </aha-modal-footer>
      </div>
    </Modal>
  );
};

export default ApplicationView(RecordLinkModal);
